import { useRef, useEffect, useCallback, useState } from "react";

// Seeded PRNG
function mulberry32(seed) {
  return function () {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Progressive digital scratch card.
 * Splits the mask into `fragmentCount` organic (Voronoi) fragments.
 * Fragment i (0-based) is assigned to a day via revealSequence.
 * Only fragments whose day <= currentDay can be scratched / are revealed.
 */
export default function ScratchCard({
  logoUrl,
  fragmentCount,
  totalDays,
  currentDay,
  revealSequence,
  launched,
  onFragmentComplete,
  revealSignal,
  hiddenWords = [],
  soundOn = false,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const stateRef = useRef({});
  const audioRef = useRef({ ctx: null, buffer: null, last: 0 });
  const soundRef = useRef(soundOn);
  useEffect(() => {
    soundRef.current = soundOn;
  }, [soundOn]);
  const [ready, setReady] = useState(false);
  const [dims, setDims] = useState({ w: 800, h: 500 });
  const [todayCentroid, setTodayCentroid] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0, show: false });

  // fragmentId (1-based) -> day (1-based)
  const fragmentDayMap = useCallback(() => {
    const map = {};
    (revealSequence || []).forEach((fragId, idx) => {
      map[fragId] = idx + 1;
    });
    return map;
  }, [revealSequence]);

  const setup = useCallback(() => {
    const wrap = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrap || !canvas) return;
    const W = Math.max(280, Math.floor(wrap.clientWidth));
    const H = Math.floor(W * 0.62);
    setDims({ w: W, h: H });
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });

    // --- Build fragment (Voronoi) map ---
    const count = fragmentCount || 45;
    const rand = mulberry32(987654321);
    const cols = Math.ceil(Math.sqrt(count * (W / H)));
    const rows = Math.ceil(count / cols);
    const seeds = [];
    for (let i = 0; i < count; i++) {
      const c = i % cols;
      const r = Math.floor(i / cols);
      const cx = ((c + 0.5) / cols) * W + (rand() - 0.5) * (W / cols) * 0.85;
      const cy = ((r + 0.5) / rows) * H + (rand() - 0.5) * (H / rows) * 0.85;
      seeds.push({ x: cx, y: cy });
    }
    const fragMap = new Uint8Array(W * H); // seed index 0..count-1
    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        let best = 0;
        let bestD = Infinity;
        for (let s = 0; s < count; s++) {
          const dx = x - seeds[s].x;
          const dy = y - seeds[s].y;
          const d = dx * dx + dy * dy;
          if (d < bestD) {
            bestD = d;
            best = s;
          }
        }
        fragMap[y * W + x] = best;
      }
    }

    // --- Draw base metallic mask (offscreen) ---
    const base = document.createElement("canvas");
    base.width = W;
    base.height = H;
    const bctx = base.getContext("2d");
    const grad = bctx.createLinearGradient(0, 0, W, H);
    grad.addColorStop(0, "#3a3a3f");
    grad.addColorStop(0.25, "#6b6b73");
    grad.addColorStop(0.5, "#4a4a50");
    grad.addColorStop(0.75, "#7d7d86");
    grad.addColorStop(1, "#39393e");
    bctx.fillStyle = grad;
    bctx.fillRect(0, 0, W, H);
    // brushed-metal horizontal grain
    for (let i = 0; i < H; i += 2) {
      bctx.strokeStyle = `rgba(255,255,255,${0.015 + Math.random() * 0.02})`;
      bctx.beginPath();
      bctx.moveTo(0, i);
      bctx.lineTo(W, i);
      bctx.stroke();
    }
    // fine granulation (blended, keeps base opaque)
    const noise = document.createElement("canvas");
    noise.width = W;
    noise.height = H;
    const nctx = noise.getContext("2d");
    const grain = nctx.createImageData(W, H);
    for (let i = 0; i < grain.data.length; i += 4) {
      const v = (Math.random() * 255) | 0;
      grain.data[i] = grain.data[i + 1] = grain.data[i + 2] = v;
      grain.data[i + 3] = 255;
    }
    nctx.putImageData(grain, 0, 0);
    bctx.globalAlpha = 0.08;
    bctx.drawImage(noise, 0, 0);
    bctx.globalAlpha = 1;
    // diagonal sheen
    const sheen = bctx.createLinearGradient(0, 0, W, H);
    sheen.addColorStop(0, "rgba(255,255,255,0)");
    sheen.addColorStop(0.45, "rgba(255,255,255,0.10)");
    sheen.addColorStop(0.55, "rgba(255,255,255,0.02)");
    sheen.addColorStop(1, "rgba(255,255,255,0)");
    bctx.fillStyle = sheen;
    bctx.fillRect(0, 0, W, H);
    // overlay text
    bctx.fillStyle = "rgba(255,255,255,0.28)";
    bctx.font = `600 ${Math.max(11, W * 0.018)}px 'JetBrains Mono', monospace`;
    bctx.textAlign = "center";
    bctx.fillText("REVELE O QUE ESTÁ SENDO CONSTRUÍDO", W / 2, H / 2 - 8);
    bctx.fillStyle = "rgba(255,255,255,0.16)";
    bctx.font = `400 ${Math.max(9, W * 0.013)}px 'JetBrains Mono', monospace`;
    bctx.fillText("UM NOVO CAPÍTULO SERÁ REVELADO A CADA DIA", W / 2, H / 2 + 16);

    const baseData = bctx.getImageData(0, 0, W, H);

    // --- Locked/reveal masks based on day ---
    const dayMap = fragmentDayMap();
    const lockedPixel = new Uint8Array(W * H);
    const initialRevealPixel = new Uint8Array(W * H);
    for (let i = 0; i < fragMap.length; i++) {
      const fragId = fragMap[i] + 1;
      const day = dayMap[fragId] || fragId;
      lockedPixel[i] = day > currentDay ? 1 : 0; // cannot scratch
      initialRevealPixel[i] = launched || day <= currentDay - 1 ? 1 : 0;
    }

    // draw base then auto-reveal past days
    ctx.putImageData(baseData, 0, 0);
    if (launched) {
      ctx.clearRect(0, 0, W, H);
    } else {
      const img = ctx.getImageData(0, 0, W, H);
      const d = img.data;
      for (let i = 0; i < initialRevealPixel.length; i++) {
        if (initialRevealPixel[i]) d[i * 4 + 3] = 0;
      }
      ctx.putImageData(img, 0, 0);
    }

    // today's fragment centroid (for highlight)
    const todayFragId = (revealSequence || [])[currentDay - 1];
    if (todayFragId && !launched) {
      const target = todayFragId - 1;
      let sx = 0,
        sy = 0,
        n = 0;
      for (let y = 0; y < H; y++) {
        for (let x = 0; x < W; x++) {
          if (fragMap[y * W + x] === target) {
            sx += x;
            sy += y;
            n++;
          }
        }
      }
      if (n > 0) setTodayCentroid({ x: (sx / n / W) * 100, y: (sy / n / H) * 100 });
    } else {
      setTodayCentroid(null);
    }

    stateRef.current = {
      W,
      H,
      ctx,
      baseData: baseData.data,
      fragMap,
      lockedPixel,
      todayFrag: todayFragId ? todayFragId - 1 : -1,
      todayScratched: 0,
      todayTotal: 0,
      completedFired: false,
    };
    // count today total pixels
    if (todayFragId) {
      let tt = 0;
      const t = todayFragId - 1;
      for (let i = 0; i < fragMap.length; i++) if (fragMap[i] === t) tt++;
      stateRef.current.todayTotal = tt;
    }
    setReady(true);
  }, [fragmentCount, currentDay, launched, revealSequence, fragmentDayMap]);

  useEffect(() => {
    setup();
    const ro = new ResizeObserver(() => setup());
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [setup]);

  const playScratch = useCallback(() => {
    const a = audioRef.current;
    if (!a.ctx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      a.ctx = new Ctx();
      const len = Math.floor(a.ctx.sampleRate * 0.4);
      const buf = a.ctx.createBuffer(1, len, a.ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
      a.buffer = buf;
    }
    if (a.ctx.state === "suspended") a.ctx.resume();
    if (performance.now() - a.last < 55) return;
    a.last = performance.now();
    const now = a.ctx.currentTime;
    const src = a.ctx.createBufferSource();
    src.buffer = a.buffer;
    src.loop = true;
    const bp = a.ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = 2600 + Math.random() * 1400;
    bp.Q.value = 0.7;
    const hp = a.ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 1200;
    const g = a.ctx.createGain();
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.055, now + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);
    src.connect(bp);
    bp.connect(hp);
    hp.connect(g);
    g.connect(a.ctx.destination);
    src.start(now);
    src.stop(now + 0.15);
  }, []);

  const eraseAt = useCallback((cx, cy) => {
    const s = stateRef.current;
    if (!s.ctx) return;
    const { W, H, ctx, fragMap, lockedPixel, baseData } = s;
    const r = Math.max(16, W * 0.028);
    // Only erase if center is on an unlocked fragment
    const ix = Math.floor(cx);
    const iy = Math.floor(cy);
    if (ix < 0 || iy < 0 || ix >= W || iy >= H) return;
    if (lockedPixel[iy * W + ix]) return; // locked area: nothing happens

    if (soundRef.current) playScratch();

    ctx.globalCompositeOperation = "destination-out";
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    g.addColorStop(0, "rgba(0,0,0,1)");
    g.addColorStop(0.7, "rgba(0,0,0,0.9)");
    g.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalCompositeOperation = "source-over";

    // Restore any locked pixels within bbox (block future fragments)
    const x0 = Math.max(0, Math.floor(cx - r));
    const y0 = Math.max(0, Math.floor(cy - r));
    const bw = Math.min(W - x0, Math.ceil(r * 2));
    const bh = Math.min(H - y0, Math.ceil(r * 2));
    if (bw > 0 && bh > 0) {
      const region = ctx.getImageData(x0, y0, bw, bh);
      const d = region.data;
      let scratched = 0;
      for (let yy = 0; yy < bh; yy++) {
        for (let xx = 0; xx < bw; xx++) {
          const mi = (y0 + yy) * W + (x0 + xx);
          const di = (yy * bw + xx) * 4;
          if (lockedPixel[mi]) {
            const bi = mi * 4;
            d[di] = baseData[bi];
            d[di + 1] = baseData[bi + 1];
            d[di + 2] = baseData[bi + 2];
            d[di + 3] = baseData[bi + 3];
          } else if (fragMap[mi] === s.todayFrag && d[di + 3] < 40) {
            scratched++;
          }
        }
      }
      ctx.putImageData(region, x0, y0);
    }
  }, [playScratch]);

  const checkTodayComplete = useCallback(() => {
    const s = stateRef.current;
    if (!s.ctx || s.todayFrag < 0 || s.completedFired || !s.todayTotal) return;
    const { W, H, ctx, fragMap } = s;
    const img = ctx.getImageData(0, 0, W, H);
    const d = img.data;
    let cleared = 0;
    for (let i = 0; i < fragMap.length; i++) {
      if (fragMap[i] === s.todayFrag && d[i * 4 + 3] < 40) cleared++;
    }
    if (cleared / s.todayTotal > 0.62) {
      s.completedFired = true;
      // fully clear the today fragment
      for (let i = 0; i < fragMap.length; i++) {
        if (fragMap[i] === s.todayFrag) d[i * 4 + 3] = 0;
      }
      ctx.putImageData(img, 0, 0);
      onFragmentComplete && onFragmentComplete();
    }
  }, [onFragmentComplete]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return {
      x: (cx / rect.width) * dims.w,
      y: (cy / rect.height) * dims.h,
      dispX: cx,
      dispY: cy,
    };
  };

  const handleDown = (e) => {
    setDrawing(true);
    const p = getPos(e);
    eraseAt(p.x, p.y);
  };
  const handleMove = (e) => {
    const p = getPos(e);
    setCursor({ x: p.dispX, y: p.dispY, show: true });
    if (!drawing) return;
    if (e.cancelable) e.preventDefault();
    eraseAt(p.x, p.y);
  };
  const handleUp = () => {
    if (drawing) checkTodayComplete();
    setDrawing(false);
  };

  // External "reveal available area" trigger
  useEffect(() => {
    if (revealSignal === undefined || revealSignal === 0) return;
    const s = stateRef.current;
    if (!s.ctx) return;
    const { W, H, ctx, lockedPixel } = s;
    const img = ctx.getImageData(0, 0, W, H);
    const d = img.data;
    for (let i = 0; i < lockedPixel.length; i++) {
      if (!lockedPixel[i]) d[i * 4 + 3] = 0;
    }
    ctx.putImageData(img, 0, 0);
    s.completedFired = true;
    onFragmentComplete && onFragmentComplete();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealSignal]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full select-none"
      data-testid="scratch-card"
      style={{ aspectRatio: "800 / 500" }}
    >
      {/* Hidden logo layer */}
      <div className="absolute inset-0 rounded-2xl overflow-hidden bg-black flex items-center justify-center">
        <img
          src={logoUrl}
          alt="Repositor Online"
          className="w-full h-full object-cover"
          crossOrigin="anonymous"
          draggable={false}
        />
        {/* hidden words distributed over the logo */}
        <div className="absolute inset-0 opacity-[0.14] pointer-events-none mix-blend-overlay">
          {hiddenWords.map((w, i) => (
            <span
              key={w}
              className="absolute font-mono uppercase tracking-widest text-black"
              style={{
                left: `${(i * 37) % 82 + 6}%`,
                top: `${(i * 53) % 78 + 8}%`,
                fontSize: `${10 + ((i * 7) % 8)}px`,
                transform: `rotate(${((i * 29) % 40) - 20}deg)`,
              }}
            >
              {w}
            </span>
          ))}
        </div>
        {launched && (
          <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 120px rgba(80,180,255,0.25)" }} />
        )}
      </div>

      {/* Scratch canvas */}
      <canvas
        ref={canvasRef}
        className="scratch-canvas absolute inset-0 w-full h-full rounded-2xl"
        style={{ opacity: launched ? 0 : 1, transition: "opacity 2s ease" }}
        onMouseDown={handleDown}
        onMouseMove={handleMove}
        onMouseUp={handleUp}
        onMouseLeave={() => {
          handleUp();
          setCursor((c) => ({ ...c, show: false }));
        }}
        onTouchStart={handleDown}
        onTouchMove={handleMove}
        onTouchEnd={handleUp}
        aria-label="Raspadinha digital progressiva. Raspe com o mouse ou dedo para revelar a parte do dia."
      />

      {/* Today fragment highlight */}
      {ready && todayCentroid && !launched && (
        <div
          className="absolute pointer-events-none"
          style={{ left: `${todayCentroid.x}%`, top: `${todayCentroid.y}%`, transform: "translate(-50%,-50%)" }}
          data-testid="today-fragment-highlight"
        >
          <span className="block h-3 w-3 rounded-full bg-white/70 shadow-[0_0_12px_rgba(255,255,255,0.8)]" />
          <span className="absolute inset-0 h-3 w-3 rounded-full bg-white/40 animate-pulse-ring" />
        </div>
      )}

      {/* custom brush cursor */}
      {cursor.show && !launched && (
        <div
          className="absolute pointer-events-none rounded-full border border-white/50"
          style={{
            left: cursor.x,
            top: cursor.y,
            width: drawing ? 44 : 30,
            height: drawing ? 44 : 30,
            transform: "translate(-50%,-50%)",
            transition: "width .15s, height .15s",
            background: "rgba(255,255,255,0.05)",
          }}
        />
      )}
    </div>
  );
}
